'use client';

import {
    useExternalStoreRuntime,
    ThreadMessageLike,
    AppendMessage,
    AssistantRuntimeProvider,
    ThreadSuggestion,
    useExternalMessageConverter,
    TextContentPart,
    ReasoningContentPart,
    SourceContentPart,
    ImageContentPart,
    FileContentPart,
    Unstable_AudioContentPart,
    MessageStatus,
    AttachmentAdapter,
    CompositeAttachmentAdapter,
    SimpleImageAttachmentAdapter,
    SimpleTextAttachmentAdapter,
    Attachment,
    CompleteAttachment,
} from '@assistant-ui/react';

import { ReadonlyJSONObject } from 'node_modules/@assistant-ui/react/src/utils/json/json-value';

import { useState, ReactNode, useEffect, useMemo, createContext, useContext, useCallback, useRef } from 'react';
import { createMastraClient } from '../lib/mastra-client';
import { useAuth } from '../../../providers/authentication-provider';
import { CoreMessage, type MessageType as Message, StorageThreadType } from '@mastra/core';
import { debounce } from '../../../../helpers/debounce';
import { MastraClient } from '@mastra/client-js';
import { AndsendModel, ThinkToolInput, thinkToolSignature } from '@andsend/services/agent/shared';
import { v4 as uuid } from '@lukeed/uuid';
import { XlsxAttachmentAdapter } from './adapters/xlsl-attachment-adapter';

type ToolCallContentPart = {
    readonly type: 'tool-call';
    readonly toolCallId?: string;
    readonly toolName: string;
    readonly args?: ReadonlyJSONObject;
    readonly argsText?: string;
    readonly artifact?: any;
    readonly result?: unknown;
    readonly isError?: boolean;
};

type ContentPart =
    | TextContentPart
    | ReasoningContentPart
    | SourceContentPart
    | ImageContentPart
    | FileContentPart
    | Unstable_AudioContentPart
    | ToolCallContentPart;

export interface ModelSettings {
    frequencyPenalty?: number;
    presencePenalty?: number;
    maxRetries?: number;
    maxSteps?: number;
    maxTokens?: number;
    temperature?: number;
    topK?: number;
    topP?: number;
    instructions?: string;
    selectedModel?: string;
}

export interface ChatProps {
    agentId: string;
    setAgentId?: (agentId: string) => void;
    agentName?: string;
    threadId?: string;
    initialMessages?: Message[];
    memory?: boolean;
    baseUrl?: string;
    refreshThreadList?: () => void;
    modelSettings?: ModelSettings;
    availableAgents?: string[];
    selectedModel?: AndsendModel;
    setSelectedModel?: (model: AndsendModel) => void;
    mastra: MastraClient;
    threads?: StorageThreadType[];
    isLoading?: boolean;
}

// Global registries to track tool calls and results across conversions
let toolCallRegistry: Record<string, ContentPart> = {};
let toolResultRegistry: Record<string, any> = {};

// Reset registries function to be called at the start of batch processing
const resetRegistries = () => {
    toolCallRegistry = {};
    toolResultRegistry = {};
};
const convertMessage = (message: ThreadMessageLike): ThreadMessageLike => {
    return message;
};

// Unified conversion method that also links tool calls with results
const convertFromMessageToThreadMessageLike = (message: Message): ThreadMessageLike => {
    // If message content is already an array of properly formatted content parts, use as is
    const contentParts: ContentPart[] = [];
    const attachments: CompleteAttachment[] = [];

    // Handle string content
    if (typeof message.content === 'string') {
        if (message.content.trim()) {
            contentParts.push({
                type: 'text',
                text: message.content,
            } as TextContentPart);
        }
    }
    // Handle array content (already formatted as content parts)
    else if (Array.isArray(message.content)) {
        (message.content as any[]).forEach((part) => {
            if (typeof part === 'object' && part !== null) {
                // Store tool results for later use
                if (part.type === 'tool-result' && 'toolCallId' in part) {
                    toolResultRegistry[part.toolCallId] = part.result;

                    // If we already know about this tool call, we can return early
                    // as this result will be merged with the call
                    if (toolCallRegistry[part.toolCallId]) {
                        (toolCallRegistry[part.toolCallId] as any).result = part.result;
                    }
                    return;
                }

                // Store tool calls for later lookup
                if (part.type === 'tool-call' && part.toolCallId) {
                    // Create a new part object instead of modifying the original
                    const newPart = {
                        ...JSON.parse(JSON.stringify(part)),
                    };

                    // Check if we have a result for this call
                    if (toolResultRegistry[part.toolCallId]) {
                        newPart.result = toolResultRegistry[part.toolCallId];
                    }

                    // Store for future reference
                    toolCallRegistry[part.toolCallId] = newPart as ContentPart;
                    contentParts.push(toolCallRegistry[part.toolCallId] as ContentPart);
                } else if (part.type === 'reasoning') {
                    contentParts.push(part as ContentPart);
                } else if (part.type === 'image') {
                    attachments.push({
                        id: uuid(),
                        type: 'image',
                        name: 'image.png',
                        contentType: 'image/png',
                        content: [part.image],
                        status: { type: 'complete' },
                    });
                } else {
                    if (part?.text?.startsWith('<file>') && part?.text?.endsWith('</file>')) {
                        const data = part.text.replace(/^<file>|<\/file>$/g, '');
                        attachments.push({
                            id: uuid(),
                            type: 'file',
                            name: 'file.txt',
                            contentType: 'text/plain',
                            content: [
                                {
                                    type: 'file' as const,
                                    data: data,
                                    mimeType: 'text/plain',
                                } as FileContentPart,
                            ],
                            status: { type: 'complete' },
                        });
                    } else {
                        contentParts.push(part as ContentPart);
                    }
                }
            }
        });
    }

    // Add tool invocations if present
    // if (message.toolInvocations?.length > 0) {
    //     message.toolInvocations.forEach((tool) => {
    //         const toolCallId =
    //             tool.toolCallId ||
    //             `${'state' in tool ? tool.state : 'call'}_${Math.random().toString(36).substring(2, 15)}`;

    //         const toolCallPart = {
    //             type: 'tool-call',
    //             toolCallId,
    //             toolName: tool.toolName,
    //             args: tool.args as ReadonlyJSONObject,
    //             isError: false,
    //             ...('result' in tool && tool.result ? { result: tool.result } : {}),
    //             ...(toolResultRegistry[toolCallId] ? { result: toolResultRegistry[toolCallId] } : {}),
    //         } as ToolCallContentPart;

    //         // Store result in registry if available
    //         if ('result' in tool && tool.result) {
    //             toolResultRegistry[toolCallId] = tool.result;
    //         }

    //         // Store for future reference
    //         toolCallRegistry[toolCallId] = toolCallPart;
    //         contentParts.push(toolCallPart);
    //     });
    // }
    if (message.type === 'tool-result') {
        return null;
    }

    return {
        id: message.id || uuid(),
        role: message.role === 'tool' ? 'assistant' : message.role,
        content: contentParts.length > 0 ? contentParts : '',
        createdAt: message.createdAt ? new Date(message.createdAt) : new Date(),
        status: message.role === 'assistant' ? { type: 'complete', reason: 'stop' } : undefined,
        attachments,
        metadata: {
            custom: {
                originalMessage: message,
            },
        },
    };
};

type MastraRuntimeContextType = {
    isRunning: boolean;
    setIsRunning: (isRunning: boolean) => void;
    messages: ThreadMessageLike[];
    setMessages: (messages: ThreadMessageLike[]) => void;
    threadUsage: Record<string, { promptTokens: number; completionTokens: number }>;
};

const MastraRuntimeContext = createContext<MastraRuntimeContextType | undefined>(undefined);

// Add helper to convert File to base64 string
function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export function MastraRuntimeProvider({
    children,
    agentId,
    initialMessages,
    agentName,
    mastra,
    memory,
    threadId,
    baseUrl,
    refreshThreadList,
    modelSettings = {},
}: Readonly<{
    children: ReactNode;
}> &
    ChatProps) {
    const [isRunning, setIsRunning] = useState(false);
    const [messages, setMessages] = useState<ThreadMessageLike[]>([]);
    const [suggestions, setSuggestions] = useState<ThreadSuggestion[]>([]);
    const [currentThreadId, setCurrentThreadId] = useState<string | undefined>(threadId);
    const [threadUsage, setThreadUsage] = useState<Record<string, { promptTokens: number; completionTokens: number }>>(
        {}
    );
    const {
        authState: { tokenResult, userId },
    } = useAuth();
    const [fixAttemptCounts, setFixAttemptCounts] = useState<Record<string, number>>({});

    useEffect(() => {
        setSuggestions([]);
    }, [currentThreadId]);

    const {
        frequencyPenalty,
        presencePenalty,
        maxRetries,
        maxSteps,
        maxTokens,
        temperature,
        topK,
        topP,
        instructions,
    } = modelSettings;

    useEffect(() => {
        const hasNewInitialMessages = initialMessages && initialMessages?.length > messages?.length;
        if (
            messages.length === 0 ||
            currentThreadId !== threadId ||
            (hasNewInitialMessages && currentThreadId === threadId)
        ) {
            if (initialMessages && threadId && memory) {
                // Reset registries before processing batch
                resetRegistries();

                // Convert messages with tool call-result linking
                const convertedMessages: ThreadMessageLike[] = initialMessages
                    .map(convertFromMessageToThreadMessageLike)
                    .filter((message) => message !== null);

                // Merge consecutive assistant messages with each other by appending their content parts
                const mergedMessages = convertedMessages.reduce((acc, message) => {
                    if (message.role === 'assistant') {
                        if (acc.length > 0 && acc[acc.length - 1].role === 'assistant') {
                            (acc[acc.length - 1].content as any) = [
                                ...(acc[acc.length - 1].content || []),
                                ...(message.content || []),
                            ];
                        } else {
                            acc.push(message);
                        }
                    } else {
                        acc.push(message);
                    }
                    return acc;
                }, [] as ThreadMessageLike[]);
                setMessages(mergedMessages);
                setCurrentThreadId(threadId);
                if (mergedMessages.length > 1) {
                    if (mergedMessages[mergedMessages.length - 1].role !== 'user') {
                        updateSuggestions();
                    }
                }
            }
        }
    }, [initialMessages, threadId, memory]);

    const agent = mastra.getAgent(agentId);

    const onNew = async (message: AppendMessage) => {
        if (message.content[0]?.type !== 'text') throw new Error('Only text messages are supported');

        const input = message.content[0].text;
        const attachments = message.attachments;

        // Check if this is a fix attempt message from the DisplayError component
        if (input.startsWith('Fix the following error:')) {
            // Extract the error message portion
            const errorText = input.replace('Fix the following error:', '').trim();
            const errorKey = errorText || 'unknown-error';

            // Increment the fix attempt counter
            setFixAttemptCounts((prev) => ({
                ...prev,
                [errorKey]: (prev[errorKey] || 0) + 1,
            }));
        }

        // Prepare attachments: convert PDF to file parts, text/CSV to text parts
        const attachmentParts = await Promise.all(
            attachments.map(async (attachment) => {
                // If a File object is provided (e.g., PDF or text file)
                if (attachment.file instanceof File) {
                    if (attachment.contentType === 'image/png' || attachment.contentType === 'image/jpeg') {
                        const base64 = await fileToBase64(attachment.file);
                        return {
                            type: 'image' as const,
                            image: base64,
                        };
                    }
                    return {
                        type: 'text' as const,
                        text: `<file>${await attachment.file.text()}</file>`,
                    };
                }
                // Fallback for adapters providing text content directly
                if (attachment.content?.[0]?.type === 'text') {
                    return {
                        type: 'text' as const,
                        text: attachment.content[0].text,
                    };
                }
                return null;
            })
        );

        const contentParts = [
            // Filter out unsupported attachments
            ...attachmentParts.filter((p): p is Exclude<typeof p, null> => p !== null),
            { type: 'text' as const, text: input },
        ];

        // Create a proper ThreadMessageLike for the user message including attachments
        const userMessage = convertFromMessageToThreadMessageLike({
            id: uuid(),
            role: 'user',
            content: contentParts,
            createdAt: new Date(),
            threadId,
            resourceId: userId,
            type: 'text',
        });

        // : ThreadMessageLike = {
        //     id: uuid(),
        //     role: 'user',
        //     content: contentParts,
        //     createdAt: new Date(),
        // };
        // Add user message to conversation state
        setMessages((currentConversation) => [...currentConversation, userMessage]);

        // Reset state for new response
        setIsRunning(true);
        setSuggestions([]);

        try {
            const response = await agent.stream({
                messages: [
                    {
                        role: 'user',
                        content: contentParts,
                    },
                ],
                frequencyPenalty,
                presencePenalty,
                maxRetries,
                maxSteps,
                maxTokens,
                temperature,
                topK,
                topP,
                instructions,
                ...(memory ? { threadId, resourceId: userId } : {}),
                toolCallStreaming: true,
            });

            if (!response.body) {
                throw new Error('No response body');
            }

            const parts: (ContentPart & { sequence: number })[] = [];
            let content = '';
            let currentTextPart = null;
            let assistantMessageAdded = false;

            await response.processDataStream({
                onTextPart(value) {
                    console.log('🤖 ~ onTextPart ~ value:', value);
                    // Update the content text - always show text immediately
                    content += value;

                    // Ensure we always have a current text part for streaming updates
                    if (!currentTextPart) {
                        // Create a new text part if none exists
                        currentTextPart = {
                            type: 'text',
                            text: value,
                            sequence: parts.length,
                        } as TextContentPart & { sequence: number };
                        parts.push(currentTextPart);
                    } else {
                        // Just update the existing text part with new content
                        currentTextPart.text = content;
                    }

                    // Update the UI immediately - this is critical for responsive updates
                    updateMessageFromParts();
                },

                async onToolCallPart(value) {
                    console.log('🤖 ~ onToolCallPart ~ value:', value);
                    // Generate a consistent tool call ID if not provided
                    const toolCallId = value.toolCallId || `call_${Math.random().toString(36).substring(2, 15)}`;

                    // Create or update tool call part
                    // Check if we already have this tool call
                    const existingToolCallIndex = parts.findIndex(
                        (p) => p.type === 'tool-call' && p.toolCallId === toolCallId
                    );

                    if (existingToolCallIndex >= 0) {
                        // Update existing tool call, preserving sequence and result if present
                        const existingPart = parts[existingToolCallIndex] as ToolCallContentPart & {
                            sequence: number;
                        };

                        // Create updated part with the new information while preserving existing data
                        const updatedPart = {
                            ...existingPart,
                            ...value,
                            toolCallId,
                            type: 'tool-call',
                            args: (value.args as ReadonlyJSONObject) || existingPart.args,
                            sequence: existingPart.sequence,
                            ...(existingPart.result ? { result: existingPart.result } : {}),
                            ...(toolResultRegistry[toolCallId] ? { result: toolResultRegistry[toolCallId] } : {}),
                        } as ToolCallContentPart & { sequence: number };

                        // Store in registry for future reference
                        toolCallRegistry[toolCallId] = updatedPart;

                        // Update the parts array
                        parts[existingToolCallIndex] = updatedPart;
                    } else {
                        // Create a new tool call part
                        const toolCallPart: ToolCallContentPart & { sequence: number } = {
                            type: 'tool-call',
                            toolCallId,
                            toolName: value.toolName,
                            args: value.args as ReadonlyJSONObject,
                            sequence: parts.length,
                            // Add result if we already have it in the registry
                            ...(toolResultRegistry[toolCallId] ? { result: toolResultRegistry[toolCallId] } : {}),
                        };

                        // Store in registry for future reference
                        toolCallRegistry[toolCallId] = toolCallPart;
                        currentTextPart = null;
                        content = '';

                        // Add the new part
                        parts.push(toolCallPart);
                    }

                    // Always update the UI after any change to parts
                    updateMessageFromParts();
                },

                async onToolResultPart(value) {
                    // Find the tool call this result belongs to
                    console.log('🤖 ~ onToolResultPart ~ value:', value);
                    const toolCallId = value.toolCallId;

                    // Store result in global registry
                    toolResultRegistry[toolCallId] = value.result;

                    const toolCallIndex = parts.findIndex((p) => p.type === 'tool-call' && p.toolCallId === toolCallId);

                    if (toolCallIndex >= 0) {
                        // Create a new object to avoid mutating read-only properties
                        const existingPart = parts[toolCallIndex] as ToolCallContentPart & { sequence: number };
                        const updatedPart = {
                            ...existingPart,
                            result: value.result,
                        } as ToolCallContentPart & { sequence: number };

                        // Update the part with its result
                        parts[toolCallIndex] = updatedPart;

                        // Update registry
                        toolCallRegistry[toolCallId] = updatedPart;
                    } else {
                        // If tool call not found, create a standalone tool call with the result
                        const resultAsTool: ToolCallContentPart & { sequence: number } = {
                            type: 'tool-call',
                            toolCallId: toolCallId,
                            toolName: '', // We don't have the tool name in this case
                            result: value.result,
                            sequence: parts.length,
                            isError: false,
                        };

                        parts.push(resultAsTool);
                        toolCallRegistry[toolCallId] = resultAsTool;
                    }

                    // Update the UI immediately
                    updateMessageFromParts();
                },

                async onReasoningPart(value) {
                    console.log('🤖 ~ onReasoningPart ~ value:', value);

                    // Check if we already have a reasoning part
                    const existingReasoningIndex = parts.findIndex((p) => p.type === 'reasoning');

                    if (existingReasoningIndex >= 0) {
                        // Update existing reasoning part by appending the new content
                        const existingPart = parts[existingReasoningIndex] as ReasoningContentPart & {
                            sequence: number;
                        };
                        parts[existingReasoningIndex] = {
                            ...existingPart,
                            text: existingPart.text + value,
                        };
                    } else {
                        // Create a new reasoning part if none exists
                        const reasoningPart = {
                            type: 'reasoning',
                            text: value,
                            sequence: parts.length,
                        } as ReasoningContentPart & { sequence: number };

                        parts.push(reasoningPart);
                    }
                    currentTextPart = null;
                    content = '';
                    // Update the UI immediately
                    updateMessageFromParts();
                },

                onErrorPart(error) {
                    throw new Error(error);
                },
                onStartStepPart(streamPart) {
                    console.log('🤖 ~ onStartStepPart ~ streamPart:', streamPart);
                },
                onFinishStepPart(streamPart) {
                    console.log('🤖 ~ onFinishStepPart ~ streamPart:', streamPart);
                },
                onDataPart(streamPart) {
                    console.log('🤖 ~ onDataPart ~ streamPart:', streamPart);
                },
                onFinishMessagePart(streamPart) {
                    console.log('🤖 ~ onFinishMessagePart ~ streamPart:', streamPart);
                    console.log('🤖 ~ onFinishMessagePart ~ streamPart.usage:', streamPart.usage);

                    if (streamPart.finishReason === 'stop') {
                        if (streamPart.usage && threadId) {
                            try {
                                setThreadUsage((prev) => {
                                    const prevThreadUsage = prev[threadId] || {
                                        promptTokens: 0,
                                        completionTokens: 0,
                                    };
                                    return {
                                        ...prev,
                                        [threadId]: {
                                            promptTokens:
                                                (streamPart.usage.promptTokens || 0) + prevThreadUsage.promptTokens,
                                            completionTokens:
                                                (streamPart.usage.completionTokens || 0) +
                                                prevThreadUsage.completionTokens,
                                        },
                                    };
                                });
                            } catch (error) {
                                console.error('Error updating thread usage', error);
                            }
                        }
                        updateSuggestions();
                    }
                },
                onToolCallStreamingStartPart(streamPart) {
                    console.log('🤖 ~ onToolCallStreamingStartPart ~ streamPart:', streamPart);
                    const toolCallPart: ToolCallContentPart & { sequence: number } = {
                        type: 'tool-call',
                        toolCallId: streamPart.toolCallId,
                        toolName: streamPart.toolName,
                        sequence: parts.length,
                    };

                    // Store in registry for future reference
                    toolCallRegistry[streamPart.toolCallId] = toolCallPart;
                    currentTextPart = null;
                    content = '';

                    // Add the new part
                    parts.push(toolCallPart);
                    updateMessageFromParts();
                },
                onMessageAnnotationsPart(streamPart) {
                    console.log('🤖 ~ onMessageAnnotationsPart ~ streamPart:', streamPart);
                },
                onToolCallDeltaPart(streamPart) {
                    console.log('🤖 ~ onToolCallDeltaPart ~ streamPart:', streamPart);

                    // Check if the tool call is a think tool call, if so, we need to update the args quickly to show the user the thought as it streams in
                    // const toolCallPart = toolCallRegistry[streamPart.toolCallId];
                    // if (toolCallPart?.type === 'tool-call' && toolCallPart?.toolName === thinkToolSignature.name) {
                    //     const updatedPart = {
                    //         ...toolCallPart,
                    //         args: {
                    //             thought: ((toolCallPart.args as ThinkToolInput)?.thought + streamPart.argsTextDelta)
                    //                 .replace(/^[^{]*{/, '')
                    //                 .replace(/}[^}]*$/, '')
                    //                 .replace(/^[^"]*"thought":\s*"/, '')
                    //                 .replace(/"\s*$/, '')
                    //                 .replace(/^undefined/, '')
                    //         },
                    //     } as unknown as ToolCallContentPart & { sequence: number; };
                    //     toolCallRegistry[streamPart.toolCallId] = updatedPart;
                    //     const toolCallIndex = parts.findIndex((p) => p.type === 'tool-call' && p.toolCallId === streamPart.toolCallId);
                    //     if (toolCallIndex >= 0) {
                    //         parts[toolCallIndex] = updatedPart;
                    //     }
                    //     updateMessageFromParts();
                    // }
                },
                onReasoningSignaturePart(streamPart) {
                    console.log('🤖 ~ onReasoningSignaturePart ~ streamPart:', streamPart);
                },
                onRedactedReasoningPart(streamPart) {
                    console.log('🤖 ~ onRedactedReasoningPart ~ streamPart:', streamPart);
                },
            });

            // Helper function to update the message based on current parts
            function updateMessageFromParts() {
                setMessages((currentConversation) => {
                    // Sort parts by their sequence number to maintain arrival order
                    const sortedParts = [...parts].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

                    // Create message content from sorted parts
                    const messageContent = sortedParts.map((part) => {
                        // Remove sequence property which is only used for sorting
                        const { sequence, ...contentWithoutSequence } = part;

                        if (part.type === 'text') {
                            const textPart = part as TextContentPart & { sequence: number };
                            if (textPart.text.startsWith('<file>') && textPart.text.endsWith('</file>')) {
                                return {
                                    type: 'file',
                                    data: textPart.text.replace(/^<file>|<\/file>$/g, ''),
                                } as FileContentPart;
                            }

                            return {
                                type: 'text',
                                text: (part as TextContentPart & { sequence: number }).text,
                            } as TextContentPart;
                        }
                        if (part.type === 'tool-call') {
                            // Create a clean object with only the properties we need
                            const toolPart = part as ToolCallContentPart & { sequence: number };
                            const toolCallPart: ToolCallContentPart = {
                                type: 'tool-call',
                                toolCallId: toolPart.toolCallId,
                                toolName: toolPart.toolName,
                                args: toolPart.args as ReadonlyJSONObject,
                                isError: toolPart.isError || false,
                            };

                            // Add result if available - create new object instead of modifying
                            if ('result' in toolPart && toolPart.result !== undefined) {
                                return {
                                    ...toolCallPart,
                                    result: toolPart.result,
                                };
                            }

                            return toolCallPart;
                        }
                        if (part.type === 'reasoning') {
                            return {
                                type: 'reasoning',
                                text: (part as ReasoningContentPart & { sequence: number }).text,
                            } as ReasoningContentPart;
                        }
                        // For other content types
                        return contentWithoutSequence as ContentPart;
                    });

                    const message: ThreadMessageLike = {
                        role: 'assistant',
                        content: messageContent as ContentPart[],
                    };

                    if (!assistantMessageAdded) {
                        assistantMessageAdded = true;
                        return [...currentConversation, message];
                    }

                    return [...currentConversation.slice(0, -1), message];
                });
            }
        } catch (error: any) {
            console.error('Error occurred in MastraRuntimeProvider', error);
            setIsRunning(false);
            const errorKey = `${error?.message || 'unknown-error'}`;
            const fixAttempts = fixAttemptCounts[errorKey] || 0;
            setMessages((currentConversation) => [
                ...currentConversation,
                {
                    role: 'assistant',
                    content: [
                        {
                            type: 'text',
                            text: `Oops, it seems something went wrong.`,
                        },
                        {
                            type: 'tool-call',
                            toolCallId: `toolu_${uuid()}`,
                            toolName: 'displayError',
                            args: {
                                error: error,
                                fixAttempts: fixAttempts,
                            },
                            isError: false,
                            result: {
                                error: error,
                                fixAttempts: fixAttempts,
                            },
                        } as ToolCallContentPart,
                    ],
                },
            ]);
        } finally {
            setIsRunning(false);
            setTimeout(() => {
                refreshThreadList?.();
            }, 500);
        }
    };

    // useEffect(() => {
    //     if (messages.length > 1) {
    //         if (messages[messages.length - 1].role !== 'user') {
    //             updateSuggestions();
    //         }
    //     }
    // }, [messages.length]);

    const updateSuggestions = useCallback(async () => {
        console.log('🤖 ~ updateSuggestions ~ starting');
        if (messages.length > 0) {
            const nextActionAgent = mastra.getAgent('nextActionAgent');
            const prompt = `
Please generate a list of 3 next actions that the user can take based on the conversation history.
`;
            const nextActionResponse = await nextActionAgent.generate({
                messages: [
                    ...messages
                        .filter((message) => message.role === 'assistant' || message.role === 'user')
                        .map(
                            (message) =>
                                ({
                                    role: message.role,
                                    content:
                                        message.content &&
                                        Array.isArray(message.content) &&
                                        message.content.filter((part) => part.type === 'text').length > 0
                                            ? message.content.filter((part) => part.type === 'text')
                                            : message.content,
                                } as CoreMessage)
                        ),
                    {
                        role: 'user',
                        content: prompt,
                    },
                ],
            });
            if (!nextActionResponse.text) {
                console.log('🤖 ~ updateSuggestions ~ no next action response');
                return;
            }

            try {
                const parsedResponse: { suggestedNextActions: string[]; reasoning: string } = JSON.parse(
                    nextActionResponse.text.replace(/^```json\s*|\s*```$/g, '')
                );
                if (!parsedResponse?.suggestedNextActions) {
                    return;
                }
                console.log('🤖 ~ updateSuggestions ~ parsedResponse:', parsedResponse);

                setSuggestions(
                    parsedResponse?.suggestedNextActions?.slice(0, 3)?.map((suggestion) => ({
                        prompt: suggestion,
                    }))
                );
                console.log('🤖 ~ updateSuggestions ~ done');
            } catch (error) {
                console.error('Error occurred in updateSuggestions', nextActionResponse?.text, error);
            }
        }
    }, [messages, mastra, setSuggestions]);

    // // Define the wrapped up runtime state for converted messages
    // const converted_messages = useExternalMessageConverter({
    //     callback: (message) => {
    //         return message;
    //     },
    //     isRunning: isRunning,
    //     messages: messages,
    // });

    const runtime = useExternalStoreRuntime<ThreadMessageLike>({
        isRunning,
        messages: messages,
        convertMessage: convertMessage,
        onAddToolResult: async (options) => {
            console.log('🤖 ~ onAddToolResult ~ options:', options);
        },
        onNew,
        suggestions,
        adapters: {
            attachments: new CompositeAttachmentAdapter([
                new SimpleImageAttachmentAdapter(),
                new SimpleTextAttachmentAdapter(),
                new XlsxAttachmentAdapter(),
            ]),
        },
    });

    return (
        <MastraRuntimeContext.Provider value={{ isRunning, setIsRunning, messages, setMessages, threadUsage }}>
            <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>
        </MastraRuntimeContext.Provider>
    );
}

export function useMastraRuntime() {
    const context = useContext(MastraRuntimeContext);
    if (!context) {
        throw new Error('useMastraRuntime must be used within a MastraRuntimeProvider');
    }
    return context;
}
